import React, { useEffect, useMemo, useState } from 'react'
import { format_day, datesAreOnSameDay, notify, format_day_only } from '../../utils/util'

import { useTranslation } from 'react-i18next'
import { camelCase } from 'lodash'
import { readSecret, isEncrypted, getSecretKeys, SECRET_KEY_EVENT } from '../../utils/crypto'
import { useUser } from '../../auth'
import { deleteById,  getPrivateNotes } from '../../server/server'
import i18n from '../../i18n'
import { ReactComponent as PrivateIcon } from '../../assets/svg/PrivateIcon.svg'
import { ReactComponent as LockIcon } from '../../assets/svg/LockIcon.svg'
import NewSecretKeyModal from '../modals/NewSecretKeyModal'
import ConfirmationModal from '../modals/ConfirmationModal'
import '../../styles/NewPrivateNotes.css'
import InfiniteScroll from 'react-infinite-scroll-component'
import { ANALYTICS_EVENTS } from '../../analytics/events'
import useTrackedSurface from '../../analytics/useTrackedSurface'

const encryptedFieldsOf = (note) =>
    [note.name, note.message, note.tomorrow_plan, ...(note.suggestions || []), ...(note.things_happened_today || []), ...(note.challenging_things || [])].filter(
        isEncrypted
    )

/**
 * Finds which key in the ring this note was written with. Every encrypted field has to
 * open, not just one: a note is written in a single pass with a single key, so partial
 * success means the key is wrong rather than the note being half readable.
 */
export const findNoteKey = (note, keys) => {
    const ciphertexts = encryptedFieldsOf(note)
    if (ciphertexts.length === 0) return { key: null, locked: false }
    const key = keys.find((candidate) => ciphertexts.every((text) => readSecret(text, candidate).readable))
    return { key: key || null, locked: !key }
}

// Resolves every encrypted field of a note up front, so a row can render a "Locked"
// state instead of the raw ciphertext when none of the known keys fit.
export const readNote = (note, keys) => {
    const { key, locked } = findNoteKey(note, keys)
    const read = (raw) => readSecret(raw, key).value

    return {
        locked,
        name: read(note.name),
        message: read(note.message),
        suggestions: (note.suggestions || []).map(read),
        thingsHappenedToday: (note.things_happened_today || []).map(read),
        challengingThings: (note.challenging_things || []).map(read),
        tomorrowPlan: read(note.tomorrow_plan),
    }
}

function PrivateNotes() {
    const [secretKeys, setSecretKeys] = useState(getSecretKeys)
    const { user } = useUser()
    const { t } = useTranslation()
    const [notes, setNotes] = useState([])
    const [secretKeyModalIsOpen, setSecretKeyModalOpen] = useState(() => getSecretKeys().length === 0)
    // The same modal both sets up a first key and unlocks notes written with an older one.
    const [unlockPrompt, setUnlockPrompt] = useState(false)
    const [selectedIndex, setSelectedIndex] = useState([])
    const [deleteNoteConfirm, setDeleteNoteConfirm] = useState(false)
    const [noteId, setNoteId] = useState('')
    const [noteIndex, setNoteIndex] = useState(-1)
    const closeModal = () => {
        setSecretKeyModalOpen(false)
        setUnlockPrompt(false)
    }
    const promptForKey = () => {
        setUnlockPrompt(true)
        setSecretKeyModalOpen(true)
    }

    const [loading, setLoading] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)
    const [resultNum, setResultNum] = useState(0)
    const [hasMore, setHasMore] = useState(true)
    useTrackedSurface(ANALYTICS_EVENTS.SETTINGS_PRIVATE_NOTES_VIEWED, { content: 'Private notes' })

    const load_notes = async (number = 0) => {
        if (resultNum === 0) {
            setLoading(true)
        } else {
            setLoadingMore(true)
        }
        const resp = await getPrivateNotes(number)
        if (resp.status == 200) {
            const results = resp.data
            const totalNotes = resultNum === 0 ? results : [...notes, ...results]
            setNotes(totalNotes)
            setLoading(false)
            setLoadingMore(false)
        }
    }

    useEffect(() => {
        load_notes(resultNum)
    }, [resultNum])

    // A key can be saved from the modal here or from the form on the Settings page.
    useEffect(() => {
        const sync = () => setSecretKeys(getSecretKeys())
        window.addEventListener(SECRET_KEY_EVENT, sync)
        return () => window.removeEventListener(SECRET_KEY_EVENT, sync)
    }, [])

    const decryptedNotes = useMemo(() => notes.map((note) => readNote(note, secretKeys)), [notes, secretKeys])

    // Only accept a key that actually opens something, so the ring does not fill with
    // typos. With nothing locked there is nothing to check it against -- a first-time
    // key, or one set before any notes exist, is always fine.
    const unlocksSomething = (key) => {
        const locked = notes.filter((note, index) => decryptedNotes[index].locked)
        return locked.length === 0 || locked.some((note) => !findNoteKey(note, [key]).locked)
    }

    const openNote = (index) => {
        let newArray = selectedIndex
        if (!selectedIndex.includes(index)) {
            setSelectedIndex([...selectedIndex, index])
            return
        } else {
            let newIndex = selectedIndex.indexOf(index)
            if (newIndex > -1) {
                newArray.splice(newIndex, 1)
            }
            setSelectedIndex([...newArray])
        }
    }
    const deleteNote = (id, index) => {
        setDeleteNoteConfirm(true)
        setNoteId(id)
        setNoteIndex(index)
    }
    const execute_deletion = async () => {
        const resp = await deleteById({ collection: 'private_notes', id: noteId })
        if (resp.status == 200) {
            openNote(noteIndex)
            if (resultNum === 0) {
                load_notes(resultNum)
            } else {
                setResultNum(0)
            }
            setDeleteNoteConfirm(false)
            notify(t('noteHasBeenDeleted'))
        }
    }
    const closeConfirmModal = () => {
        setDeleteNoteConfirm(false)
    }
    const NotePreview = ({ note, content, index }) => {
        const yesterdayEmotion = note.last_daily_feedback?.emotion
        const todayEmotion = note.today_daily_feedback?.emotion

        return (
            <div className="new-note-preview">
                <div className="new-note-item" onClick={() => openNote(index)} style={{ backgroundColor: selectedIndex.includes(index) ? '#ECEBF8' : '#f8f8fb' }}>
                    {content.locked ? (
                        <span className="new-note-item-locked">
                            <LockIcon />
                            <p className="new-note-item-type new-note-item-type-locked">{t('locked')}</p>
                        </span>
                    ) : (
                        <p className="new-note-item-type">{content.name || t('Day review')}</p>
                    )}
                    <div style={{ flex: 1 }}></div>
                    <p className="new-note-item-date">{format_day(note.created_date, true, i18n.language)}</p>
                    <div className="new-note-select" style={{ transform: selectedIndex.includes(index) ? 'rotate(180deg)' : 'none' }}>
                        {/* <AngleUpIcon /> */}
                    </div>
                </div>
                {selectedIndex.includes(index) && content.locked && (
                    <div className="new-note-opened">
                        <div className="new-note-locked-panel">
                            <LockIcon className="new-note-locked-panel-icon" />
                            <p className="new-note-locked-hint">{t('thisNoteIsEncryptedEnterYourSecretKeyToReadIt')}</p>
                            <button type="button" className="new-note-unlock" id={`unlock-note-${note.id}`} onClick={promptForKey}>
                                {t('enterSecretKey')}
                            </button>
                        </div>
                        <div className="new-note-delete" id={`delete-note-locked-${note.id}`} onClick={() => deleteNote(note.id, index)}>
                            <p>{t('deleteNote')}</p>
                        </div>
                    </div>
                )}
                {selectedIndex.includes(index) && !content.locked && note.type === 'daily_feedback' && (
                    <div className="new-note-opened">
                        <p className="new-note-opened-subtitle">{t('doYouThinkThingsAreForAnyReasonInParticular', { result: t(camelCase(note.compare)).toLowerCase() })}</p>
                        <div className="new-note-compare-answer">
                            <p className="new-note-compare-message">{content.message}</p>
                        </div>
                        <p className="new-note-opened-subtitle" style={{ marginTop: 21 }}></p>
                        <div className="note-suggestions">
                            {content.suggestions.map((suggestion, suggestionIndex) => {
                                return (
                                    <div className="new-note-suggestion-item w-full flex justify-start items-start" key={suggestionIndex}>
                                        <p className="new-note-suggestion-text text-left">
                                            0{suggestionIndex + 1} {suggestion}
                                        </p>
                                    </div>
                                )
                            })}
                        </div>
                        <div className="new-note-delete" id={`delete-note-daily-feedback-${note.id}`} onClick={() => deleteNote(note.id, index)}>
                            <p>{t('deleteNote')}</p>
                        </div>
                    </div>
                )}
                {selectedIndex.includes(index) && !content.locked && note.type === 'day_reflection' && (
                    <div className="new-note-opened">
                        <p className="new-note-opened-subtitle">{t('whatAreJust3GoodThingsThatHappenedToday')}</p>
                        <div className="note-suggestions" style={{ marginTop: 5 }}>
                            {content.thingsHappenedToday.map((thing, thingIndex) => {
                                return (
                                    <div className="new-note-suggestion-item w-full flex justify-start items-start" key={thingIndex}>
                                        <p className="new-note-suggestion-text text-left">
                                            0{thingIndex + 1} {thing}
                                        </p>
                                    </div>
                                )
                            })}
                        </div>
                        <p className="new-note-opened-subtitle" style={{ marginTop: 20 }}>
                            {t('What are 3 things that are a bit challenging at the moment?')}
                        </p>
                        <div className="note-suggestions" style={{ marginTop: 5 }}>
                            {content.challengingThings.map((thing, thingIndex) => {
                                return (
                                    <div className="new-note-suggestion-item w-full flex justify-start items-start" key={thingIndex}>
                                        <p className="new-note-suggestion-text text-left">
                                            0{thingIndex + 1} {thing}
                                        </p>
                                    </div>
                                )
                            })}
                        </div>
                        <p className="new-note-opened-subtitle" style={{ marginTop: 20 }}>
                            {t('What’s just one thing you will do differently tomorrow?')}
                        </p>
                        <div className="new-note-compare-answer" style={{ marginTop: 10 }}>
                            <p className="new-note-compare-message">{content.tomorrowPlan}</p>
                        </div>
                        <div className="new-note-delete" id={`delete-note-day-reflection-${note.id}`} onClick={() => deleteNote(note.id, index)}>
                            <p>{t('deleteNote')}</p>
                        </div>
                    </div>
                )}
            </div>
        )
    }

    return (
        <div className="new-notes-container" style={{ width: '100%' }}>
            <p className="new-notes-title text-xl text-deepPurple font-bold mb-6">{t('yourPrivateNotes')}</p>
            <div className="new-notes-subtitle flex items-center justify-center bg-containerGray rounded-xl mx-8 p-2">
                <PrivateIcon />
                <p className="ml-2 new-notes-text text-mudPurple"> {t('thisIsYourSafePlaceAndNothingWillBeShared')}</p>
            </div>
            <div className="w-full">
                <InfiniteScroll
                    dataLength={notes?.length}
                    next={() => {
                        if (!loading && !loadingMore) {
                            setResultNum(notes?.length)
                        }
                    }}
                    hasMore={hasMore}
                    scrollableTarget="settings-scroll-ref"
                    style={{ overflowX: 'hidden', overflowY: 'auto', width: '100%' }}
                    scrollThreshold={0.8}
                >
                    {notes.map((note, index) => {
                        const previousNote = index > 0 && notes[index - 1]
                        const previousNoteDay = previousNote?.created_date
                        const sameDay = !!previousNoteDay && datesAreOnSameDay(new Date(note.created_date), new Date(previousNoteDay))

                        return (
                            <div key={index} className="new-notes-group">
                                {!sameDay && (
                                    <div className="new-notes-dates" style={{ direction: 'ltr' }}>
                                        {format_day_only(note.created_date, true, i18n.language)}
                                    </div>
                                )}
                                <NotePreview note={note} content={decryptedNotes[index]} index={index} />
                            </div>
                        )
                    })}
                    {!loading && notes.length === 0 && <p className="text-center text-deepPurple text-base font-semibold w-full mt-20">{t('No items found')}</p>}
                </InfiniteScroll>
            </div>

            <NewSecretKeyModal
                modalIsOpen={secretKeyModalIsOpen}
                closeModal={closeModal}
                title={unlockPrompt ? t('unlockYourNotes') : undefined}
                subtitle={unlockPrompt ? t('enterTheSecretKeyYouUsedForTheseNotes') : undefined}
                onValidate={unlocksSomething}
            />
            <ConfirmationModal
                modalIsOpen={deleteNoteConfirm}
                closeModal={closeConfirmModal}
                message={t('confirmDeletion')}
                subMessage={t('deleteYourPrivateNote')}
                confirmation={execute_deletion}
            />
        </div>
    )
}

export default PrivateNotes
